import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import Reveal from './Reveal'

export default function Cursos() {
  const [cursos, setCursos] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('https://www.medicinacondante.com/cursos.php')
      .then((r) => r.json())
      .then((data) => {
        setCursos(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const showPrice = (precio) => {
    if (!precio) return false
    const n = parseFloat(precio)
    if (isNaN(n) || n === 0) return false
    return true
  }

  return (
    <section
      id="cursos"
      className="relative overflow-hidden"
      style={{ padding: '120px 5vw', background: 'var(--bg-2)' }}
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, #C9A84C 0, #C9A84C 1px, transparent 1px, transparent 80px), repeating-linear-gradient(90deg, #C9A84C 0, #C9A84C 1px, transparent 1px, transparent 80px)',
        }}
      />

      <div className="relative z-10">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            {/* Título */}
            <div className="shrink-0">
              <div className="section-label">Cursos disponibles</div>
              <h2
                className="font-display font-black leading-[1.04] tracking-[-0.02em]"
                style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', color: 'var(--ink)' }}
              >
                Formación diseñada
                <br />
                para{' '}
                <em className="not-italic" style={{ color: 'var(--gold)' }}>
                  resultados reales
                </em>
                .
              </h2>
            </div>

            {/* Texto derecho */}
            <div
              className="flex flex-col gap-4 max-w-sm"
              style={{ borderLeft: '2px solid rgba(201,168,76,0.25)', paddingLeft: 20 }}
            >
              <p className="font-body font-light text-[13px] leading-[1.8]" style={{ color: 'var(--ink-2)' }}>
                Cada curso está pensado como un entrenamiento: primero ordenás el tema,
                después lo razonás y finalmente lo llevás a preguntas reales.
              </p>
              <p className="font-body font-light text-[13px] leading-[1.8]" style={{ color: 'var(--ink-2)' }}>
                Dejás de estudiar temas aislados y empezás a ver el sistema completo:
                qué importa, cómo se conecta y cómo te lo pueden tomar.
              </p>
              <p className="font-body font-light text-[13px] leading-[1.8]" style={{ color: 'var(--ink-2)' }}>
                Te doy un mapa claro de qué estudiar, cómo estudiarlo, cómo razonarlo
                y cómo responder en el examen.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-24">
            <div
              style={{
                width: 36, height: 36, borderRadius: '50%',
                border: '1px solid rgba(201,168,76,0.2)',
                borderTopColor: '#C9A84C',
                animation: 'cursos-spin 0.9s linear infinite',
              }}
            />
            <style>{`@keyframes cursos-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Grid */}
        {!loading && cursos.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-3"
            style={{ gap: '1px', background: 'var(--line)' }}
          >
            {cursos.map((c, i) => (
              <motion.div
                key={c.id ?? c.nombre_curso}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ backgroundColor: 'rgba(22,22,22,1)' }}
                className="relative flex flex-col overflow-hidden"
                style={{ background: 'var(--bg-2)' }}
              >
                {/* Image header */}
                <div
                  className="relative overflow-hidden"
                  style={{ height: '320px', background: 'var(--bg-3)', borderBottom: '1px solid var(--line)' }}
                >
                  {c.imagen ? (
                    <>
                      <img
                        src={c.imagen}
                        alt={c.nombre_curso}
                        className="absolute inset-0 w-full h-full"
                        style={{ objectFit: 'cover', objectPosition: 'center' }}
                      />
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.12) 45%, rgba(0,0,0,0.04) 100%)',
                        }}
                      />
                    </>
                  ) : (
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.06) 0%, transparent 60%)' }}
                    >
                      <svg width="52" height="52" viewBox="0 0 52 52" fill="none" opacity="0.35">
                        <circle cx="26" cy="26" r="16" stroke="#C9A84C" strokeWidth="1.2" />
                        <circle cx="26" cy="26" r="6" stroke="#C9A84C" strokeWidth="1" />
                        <line x1="26" y1="10" x2="26" y2="6" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
                        <line x1="26" y1="46" x2="26" y2="42" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
                        <line x1="10" y1="26" x2="6" y2="26" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
                        <line x1="46" y1="26" x2="42" y2="26" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="flex flex-col flex-1 p-8">
                  <h3
                    className="font-display font-black text-[18px] leading-[1.2] mb-2.5"
                    style={{ color: 'var(--ink)' }}
                  >
                    {c.nombre_curso}
                  </h3>

                  {c.descripcion && (
                    <p
                      className="font-body font-light text-[13px] leading-[1.75] flex-1"
                      style={{ color: 'var(--ink-2)' }}
                    >
                      {c.descripcion}
                    </p>
                  )}

                  {/* Footer */}
                  <div
                    className="flex items-center justify-between mt-6 pt-5"
                    style={{ borderTop: '1px solid var(--line)' }}
                  >
                    {showPrice(c.precio) ? (
                      <span className="font-display font-black text-[20px]" style={{ color: 'var(--gold)' }}>
                        ${c.precio}
                      </span>
                    ) : (
                      <div />
                    )}

                    {c.link && (
                      <motion.a
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-display font-black text-[10px] tracking-[0.1em] uppercase px-5 py-2.5"
                        style={{ background: 'var(--gold)', color: '#000' }}
                        whileHover={{ backgroundColor: '#E2C47A' }}
                        transition={{ duration: 0.15 }}
                      >
                        Comprar
                      </motion.a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && cursos.length === 0 && (
          <div className="flex flex-col items-center py-24 gap-4">
            <p className="font-body font-light text-[13px]" style={{ color: 'var(--ink-3)' }}>
              No hay cursos disponibles por el momento.
            </p>
          </div>
        )}
      </div>
    </section>
  )
}